package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.enginekartei.EngineKarteiField
import org.example.project.enginekartei.EngineKarteiFieldType
import org.example.project.service.LoginService
import org.example.project.setup.SetupApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

val standortverwaltungKarteiPages = emptyList<All>()

private fun standortField(id: String, fallback: String = id): EngineKarteiField =
    EngineKarteiField(id, fallback, type = EngineKarteiFieldType.TEXT)

internal val standortStructureFields = listOf(
    standortField("halleVon", "Halle von"),
    standortField("halleBis", "Halle bis"),
    standortField("regalVon", "Regal von"),
    standortField("regalBis", "Regal bis"),
    standortField("fachVon", "Fach von"),
    standortField("fachBis", "Fach bis"),
    standortField("ebeneVon", "Ebene von"),
    standortField("ebeneBis", "Ebene bis"),
    standortField("toreVon", "Tor von"),
    standortField("toreBis", "Tor bis"),
    standortField("parkplatzVon", "Parkplatz von"),
    standortField("parkplatzBis", "Parkplatz bis")
)

private val standortAddressFields = listOf(
    "standort" to "Standort",
    "strasse" to "Straße",
    "hausnummer" to "Hausnummer",
    "plz" to "PLZ",
    "ort" to "Ort",
    "land" to "Land"
)

private val standortContactFields = listOf(
    "telefon" to "Telefon",
    "email" to "E-Mail",
    "webseite" to "Webseite"
)

private val standortStructureGroups = listOf(
    "Hallen" to listOf("halleVon" to "Halle von", "halleBis" to "Halle bis"),
    "Regale" to listOf("regalVon" to "Regal von", "regalBis" to "Regal bis"),
    "Fächer" to listOf("fachVon" to "Fach von", "fachBis" to "Fach bis"),
    "Ebenen" to listOf("ebeneVon" to "Ebene von", "ebeneBis" to "Ebene bis"),
    "Tore" to listOf("toreVon" to "Tor von", "toreBis" to "Tor bis"),
    "Parkplaetze" to listOf("parkplatzVon" to "Parkplatz von", "parkplatzBis" to "Parkplatz bis")
)

@Composable
fun CUSStandortverwaltung() {
    val scope = rememberCoroutineScope()
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var firmenId by remember { mutableStateOf("") }
    var firmenPosition by remember { mutableStateOf("") }
    var selectedId by remember { mutableStateOf("") }
    var form by remember { mutableStateOf(newStandort("")) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }
    var departmentInput by remember { mutableStateOf("") }
    var layoutHover by remember { mutableStateOf(false) }
    var layoutPressed by remember { mutableStateOf(false) }

    fun openStandortLayout() {
        val baseHash = window.location.hash.substringBefore("?").ifBlank { "#/dashboard" }
        window.location.hash = "$baseHash?screen=StandortLayoutVerwaltung"
    }

    suspend fun reload(keepId: String = selectedId) {
        loading = true
        firmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
        val loaded = runCatching {
            SetupApi.loadCusStandorte().sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        }.getOrDefault(emptyList())
        firmenPosition = runCatching { SetupApi.loadCusCompany()?.position.orEmpty() }.getOrDefault("")
        standorte = loaded
        val nextId = keepId.ifBlank { loaded.firstOrNull()?.id.orEmpty() }
        selectedId = nextId
        form = loaded.firstOrNull { it.id == nextId } ?: newStandort(firmenId)
        loading = false
    }

    fun updateText(field: String, value: String) {
        form = when (field) {
            "standort" -> form.copy(standort = value)
            "strasse" -> form.copy(strasse = value)
            "hausnummer" -> form.copy(hausnummer = value)
            "plz" -> form.copy(plz = value)
            "ort" -> form.copy(ort = value)
            "land" -> form.copy(land = value)
            "telefon" -> form.copy(telefon = value)
            "email" -> form.copy(email = value)
            "webseite" -> form.copy(webseite = value)
            "halleVon" -> form.copy(halleVon = value)
            "halleBis" -> form.copy(halleBis = value)
            "regalVon" -> form.copy(regalVon = value)
            "regalBis" -> form.copy(regalBis = value)
            "fachVon" -> form.copy(fachVon = value)
            "fachBis" -> form.copy(fachBis = value)
            "ebeneVon" -> form.copy(ebeneVon = value)
            "ebeneBis" -> form.copy(ebeneBis = value)
            "toreVon" -> form.copy(toreVon = value)
            "toreBis" -> form.copy(toreBis = value)
            "parkplatzVon" -> form.copy(parkplatzVon = value)
            "parkplatzBis" -> form.copy(parkplatzBis = value)
            else -> form
        }
    }

    fun save() {
        if (saving) return
        saving = true
        message = ""
        scope.launch {
            val state = form.copy(
                firmenId = form.firmenId.ifBlank { firmenId },
                land = form.land.ifBlank { "DE" },
                firma = "",
                geschaeftsfuehrer = "",
                webseite = form.webseite,
                gegruendetAm = ""
            )
            val saved = runCatching { SetupApi.saveCusStandort(firmenId, state) }.getOrNull()
            if (saved == null) {
                message = "Standort konnte nicht gespeichert werden."
            } else {
                message = "Standort gespeichert."
                reload(saved.id.ifBlank { state.id })
            }
            saving = false
        }
    }

    fun addDepartment() {
        val departmentName = departmentInput.trim()
        if (departmentName.isBlank()) return
        val existing = form.abteilungen.map { it.name.ifBlank { it.bezeichnung }.trim().lowercase() }.toSet()
        if (departmentName.lowercase() in existing) {
            departmentInput = ""
            return
        }
        val nextDepartment = All(
            id = nextDepartmentId(form.abteilungen),
            name = departmentName,
            bezeichnung = departmentName
        )
        form = form.copy(abteilungen = form.abteilungen + nextDepartment)
        departmentInput = ""
        message = ""
    }

    fun removeDepartment(id: String, name: String) {
        form = form.copy(
            abteilungen = form.abteilungen.filterNot {
                it.id == id && it.name.ifBlank { it.bezeichnung } == name
            }
        )
        message = ""
    }

    LaunchedEffect(Unit) { reload() }

    Style(StandortverwaltungStyles)
    Div({ classes(StandortverwaltungStyles.page) }) {
        Div({ classes(StandortverwaltungStyles.shell) }) {
            Div({ classes(StandortverwaltungStyles.topbar) }) {
                Div({ classes(StandortverwaltungStyles.header) }) {
                    Div({ classes(StandortverwaltungStyles.iconBox) }) {
                        Img(src = "images/domain.svg", attrs = { classes(StandortverwaltungStyles.icon) })
                    }
                    Div({ classes(StandortverwaltungStyles.headerText) }) {
                        Div({ classes(StandortverwaltungStyles.titleRow) }) {
                            H3({ classes(StandortverwaltungStyles.title) }) {
                                Text(form.standort.ifBlank { "Standortverwaltung" })
                                if (firmenPosition.isNotBlank()) {
                                    Span({ classes(StandortverwaltungStyles.networkPosition) }) {
                                        Text(" - $firmenPosition")
                                    }
                                }
                            }
                        }
                    }
                }
                Div({ classes(StandortverwaltungStyles.actions) }) {
                    Div({ classes(StandortverwaltungStyles.dropdownWrap) }) {
                        AppDropdown(
                            options = standorte.map { All(key = it.id, label = standortLabel(it)) },
                            selectedValue = selectedId.ifBlank { null },
                            onChange = { id ->
                                selectedId = id
                                form = standorte.firstOrNull { it.id == id } ?: newStandort(firmenId)
                                departmentInput = ""
                                message = ""
                            },
                            placeholder = if (loading) "Standorte werden geladen" else "Standort wählen",
                            appearance = AppDropdownAppearance.Surface,
                            attrs = {
                                style {
                                    width(100.percent)
                                    minHeight(36.px)
                                }
                            }
                        )
                    }
                    LumaticaButton(
                        label = "Neu",
                        onClick = {
                            selectedId = ""
                            form = newStandort(firmenId)
                            departmentInput = ""
                            message = ""
                        },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 36
                    )
                    LumaticaButton(
                        label = if (saving) "Speichern..." else "Speichern",
                        onClick = ::save,
                        variant = LumaticaButtonVariant.Action,
                        enabled = !saving,
                        heightPx = 36
                    )
                    Div({
                        classes(StandortverwaltungStyles.layoutButton)
                        attr("aria-label", "Standort Layout")
                        onMouseEnter { layoutHover = true }
                        onMouseLeave { layoutHover = false }
                        onClick {
                            if (layoutPressed) return@onClick
                            layoutPressed = true
                            scope.launch {
                                delay(120)
                                openStandortLayout()
                                layoutPressed = false
                            }
                        }
                    }) {
                        Img(src = "images/brush.svg", attrs = {
                            classes(
                                if (layoutPressed) StandortverwaltungStyles.layoutButtonIconActive
                                else StandortverwaltungStyles.layoutButtonIcon
                            )
                        })
                        if (layoutHover) {
                            Span({ classes(StandortverwaltungStyles.layoutButtonHoverText) }) { Text("Standort Layout") }
                        }
                    }
                }
            }

            if (message.isNotBlank()) {
                Span({ classes(StandortverwaltungStyles.message) }) { Text(message) }
            }

            Div({ classes(StandortverwaltungStyles.content) }) {
                Div({ classes(StandortverwaltungStyles.surface) }) {
                    StandortFormSection(
                        title = "Adresse",
                        groups = listOf("Standort" to standortAddressFields),
                        form = form,
                        onChange = ::updateText
                    )
                    StandortFormSection(
                        title = "Kontakt",
                        groups = listOf("Kommunikation" to standortContactFields),
                        form = form,
                        onChange = ::updateText
                    )
                    StandortFormSection(
                        title = "Struktur",
                        groups = standortStructureGroups,
                        form = form,
                        onChange = ::updateText
                    )
                    StandortDepartmentsSection(
                        departments = form.abteilungen,
                        input = departmentInput,
                        onInputChange = { departmentInput = it },
                        onAdd = ::addDepartment,
                        onRemove = ::removeDepartment
                    )
                    StandortPalettenSection(
                        form = form,
                        onChange = { field, checked ->
                            form = when (field) {
                                "palettentauschAktiv" -> form.copy(palettentauschAktiv = checked)
                                "palettentauschWeAktiv" -> form.copy(palettentauschWeAktiv = checked)
                                "palettentauschWaAktiv" -> form.copy(palettentauschWaAktiv = checked)
                                else -> form
                            }
                        }
                    )
                }
            }
        }

    }
}

@Composable
private fun StandortFormSection(
    title: String,
    groups: List<Pair<String, List<Pair<String, String>>>>,
    form: All,
    onChange: (String, String) -> Unit
) {
    Div({ classes(StandortverwaltungStyles.sectionBlock) }) {
        Div({ classes(StandortverwaltungStyles.sectionTitle) }) { Text(title) }
        groups.forEach { (groupTitle, fields) ->
            Div({ classes(StandortverwaltungStyles.groupBlock) }) {
                Div({ classes(StandortverwaltungStyles.groupTitle) }) { Text(groupTitle) }
                Div({ classes(StandortverwaltungStyles.fieldGrid) }) {
                    fields.forEach { (field, label) ->
                        Div({ classes(StandortverwaltungStyles.fieldRow) }) {
                            Span({ classes(StandortverwaltungStyles.fieldLabel) }) { Text(label) }
                            StandortTextInput(
                                value = standortFieldValue(form, field),
                                onChange = { onChange(field, it) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StandortDepartmentsSection(
    departments: List<All>,
    input: String,
    onInputChange: (String) -> Unit,
    onAdd: () -> Unit,
    onRemove: (String, String) -> Unit
) {
    Div({ classes(StandortverwaltungStyles.sectionBlock) }) {
        Div({ classes(StandortverwaltungStyles.sectionTitle) }) { Text("Abteilungen") }
        Div({ classes(StandortverwaltungStyles.groupBlock) }) {
            Div({ classes(StandortverwaltungStyles.groupTitle) }) { Text("Abteilungen") }
            Div({ classes(StandortverwaltungStyles.departmentComposer) }) {
                StandortTextInput(value = input, onChange = onInputChange)
                LumaticaButton(
                        label = "Hinzufügen",
                    onClick = onAdd,
                    variant = LumaticaButtonVariant.Standard,
                    enabled = input.isNotBlank(),
                    heightPx = 34
                )
            }
            if (departments.isEmpty()) {
                Span({ classes(StandortverwaltungStyles.emptyText) }) { Text("Keine Abteilungen angelegt.") }
            } else {
                Div({ classes(StandortverwaltungStyles.chipList) }) {
                    departments.forEach { department ->
                        val id = department.id
                        val name = department.name.ifBlank { department.bezeichnung }
                        if (name.isNotBlank()) {
                            Div({ classes(StandortverwaltungStyles.chip) }) {
                                Span { Text(name) }
                                Span({
                                    classes(StandortverwaltungStyles.chipRemove)
                                    onClick { onRemove(id, name) }
                                }) { Text("x") }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StandortPalettenSection(
    form: All,
    onChange: (String, Boolean) -> Unit
) {
    val fields = listOf(
        "palettentauschAktiv" to ("Palettentausch" to form.palettentauschAktiv),
        "palettentauschWeAktiv" to ("Wareneingang" to form.palettentauschWeAktiv),
        "palettentauschWaAktiv" to ("Warenausgang" to form.palettentauschWaAktiv)
    )
    Div({ classes(StandortverwaltungStyles.sectionBlock) }) {
        Div({ classes(StandortverwaltungStyles.sectionTitle) }) { Text("Palettentausch") }
        Div({ classes(StandortverwaltungStyles.toggleGrid) }) {
            fields.forEach { (field, data) ->
                Div({ classes(StandortverwaltungStyles.toggleRow) }) {
                    Span({ classes(StandortverwaltungStyles.fieldLabel) }) { Text(data.first) }
                    LumaticaCheckbox(
                        checked = data.second,
                        onChange = { onChange(field, it) },
                        fontSizePx = 20
                    )
                }
            }
        }
    }
}

@Composable
private fun StandortTextInput(value: String, onChange: (String) -> Unit) {
    Input(InputType.Text, attrs = {
        classes(StandortverwaltungStyles.input)
        value(value)
        placeholder("")
        onInput { onChange(it.value) }
    })
}

private fun standortFieldValue(form: All, field: String): String =
    when (field) {
        "standort" -> form.standort
        "strasse" -> form.strasse
        "hausnummer" -> form.hausnummer
        "plz" -> form.plz
        "ort" -> form.ort
        "land" -> form.land
        "telefon" -> form.telefon
        "email" -> form.email
        "webseite" -> form.webseite
        "halleVon" -> form.halleVon
        "halleBis" -> form.halleBis
        "regalVon" -> form.regalVon
        "regalBis" -> form.regalBis
        "fachVon" -> form.fachVon
        "fachBis" -> form.fachBis
        "ebeneVon" -> form.ebeneVon
        "ebeneBis" -> form.ebeneBis
        "toreVon" -> form.toreVon
        "toreBis" -> form.toreBis
        "parkplatzVon" -> form.parkplatzVon
        "parkplatzBis" -> form.parkplatzBis
        else -> ""
    }

private fun standortLabel(standort: All): String =
    standort.standort.ifBlank { standort.id.ifBlank { "Neuer Standort" } }

private fun nextDepartmentId(departments: List<All>): String =
    ((departments.mapNotNull { it.id.filter(Char::isDigit).toIntOrNull() }.maxOrNull() ?: 0) + 1).toString()

private fun newStandort(firmenId: String): All =
    All(
        id = "neu",
        firmenId = firmenId,
        land = "DE",
        palettentauschAktiv = true,
        palettentauschWeAktiv = true,
        palettentauschWaAktiv = true
    )

private object StandortverwaltungStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        padding(18.px, 22.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
        color(Color.white)
        overflow("hidden")
    }
    val shell by style {
        width(100.percent)
        maxWidth(1320.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val topbar by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        flexWrap(FlexWrap.Nowrap)
        gap(12.px)
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
    }
    val header by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        gap(10.px)
        minWidth(0.px)
        flexShrink(0)
    }
    val iconBox by style {
        width(72.px)
        height(72.px)
        borderRadius(6.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(rgba(255, 255, 255, 0.07))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        boxSizing("border-box")
    }
    val icon by style {
        width(38.px)
        height(38.px)
        property("filter", "brightness(0) invert(1)")
        opacity(0.88)
    }
    val headerText by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.FlexStart)
        gap(5.px)
        minWidth(0.px)
    }
    val titleRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
        flexWrap(FlexWrap.Wrap)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val networkPosition by style {
        color(rgba(255, 255, 255, 0.82))
        fontSize(17.px)
        fontWeight("850")
    }
    val actions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
        flexWrap(FlexWrap.Nowrap)
        property("flex", "1 1 auto")
        minWidth(0.px)
    }
    val dropdownWrap by style {
        width(260.px)
        maxWidth(100.percent)
        property("flex", "0 1 260px")
    }
    val layoutButton by style {
        position(Position.Relative)
        width(34.px)
        height(36.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(0.px)
        border(0.px)
        borderRadius(0.px)
        backgroundColor(Color.transparent)
        boxSizing("border-box")
        cursor("pointer")
        flexShrink(0)
        self + ":hover img" style {
            property("filter", UISettings.STATE_BUTTON_ACTIVE_ICON_FILTER)
        }
    }
    val layoutButtonIcon by style {
        width(18.px)
        height(18.px)
        property("filter", UISettings.STATE_BUTTON_ICON_FILTER)
        opacity(0.86)
    }
    val layoutButtonIconActive by style {
        width(18.px)
        height(18.px)
        property("filter", UISettings.STATE_BUTTON_ACTIVE_ICON_FILTER)
        opacity(1)
    }
    val layoutButtonHoverText by style {
        position(Position.Absolute)
        left(30.px)
        top(4.px)
        property("z-index", "20")
        property("pointer-events", "none")
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("box-shadow", "0 12px 28px ${DashboardTheme.SURFACE_SHADOW}")
        borderRadius(6.px)
        padding(6.px, 9.px)
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("600")
        property("white-space", "nowrap")
    }
    val message by style {
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("700")
    }
    val content by style {
        minHeight(0.px)
        overflowY("auto")
        paddingRight(4.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(22.px)
    }
    val surface by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val sectionBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        padding(22.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        boxSizing("border-box")
    }
    val sectionTitle by style {
        marginTop(0.px)
        marginBottom(8.px)
        color(rgba(255, 255, 255, 0.88))
        fontSize(17.px)
        fontWeight("900")
        textAlign("center")
    }
    val groupBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        minWidth(0.px)
        minHeight(0.px)
        paddingBottom(24.px)
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        self + ":last-child" style {
            paddingBottom(0.px)
            property("border-bottom", "0")
        }
    }
    val groupTitle by style {
        color(rgba(255, 255, 255, 0.78))
        fontSize(13.px)
        fontWeight("800")
    }
    val fieldGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(980.px)) {
            self style {
                property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
            }
        }
        media(mediaMaxWidth(640.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val fieldRow by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val fieldLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(10.px)
        fontWeight("800")
        property("text-transform", "uppercase")
        letterSpacing(0.px)
    }
    val input by style {
        height(34.px)
        width(100.percent)
        boxSizing("border-box")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 10.px)
        fontSize(12.px)
        property("outline", "none")
        self + ":focus" style {
            property("border-color", UISettings.ACCENT.toString())
            backgroundColor(rgba(255, 255, 255, 0.09))
        }
    }
    val departmentComposer by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(0, 1fr) auto")
        gap(8.px)
        alignItems(AlignItems.Center)
        media(mediaMaxWidth(640.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val chipList by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        flexWrap(FlexWrap.Wrap)
    }
    val chip by style {
        minHeight(32.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        padding(0.px, 9.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        fontSize(12.px)
        fontWeight("800")
        boxSizing("border-box")
    }
    val chipRemove by style {
        width(18.px)
        height(18.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        borderRadius(4.px)
        backgroundColor(rgba(0, 0, 0, 0.22))
        color(rgba(255, 255, 255, 0.72))
        cursor("pointer")
        fontSize(11.px)
        fontWeight("900")
        property("user-select", "none")
    }
    val emptyText by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(12.px)
        fontWeight("700")
    }
    val toggleGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(760.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val toggleRow by style {
        minHeight(42.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        padding(0.px, 12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.05))
        boxSizing("border-box")
    }
}
